/**
 * API Configuration
 * Centralized backend URL management with simple target selection
 */

// ============================================================================
// BACKEND TARGET TYPES
// ============================================================================

type BackendTarget = 'local' | 'dev' | 'staging' | 'prod';

/**
 * Get the current backend target from environment variable
 * Defaults to 'local' if not set or invalid
 */
const getBackendTarget = (): BackendTarget => {
  const target = import.meta.env.VITE_BACKEND_TARGET as string;

  if (target === 'local' || target === 'dev' || target === 'staging' || target === 'prod') {
    return target;
  }

  // Default to local if invalid or not set
  return 'local';
};

// ============================================================================
// BACKEND URL SELECTION
// ============================================================================

/**
 * Get the API base URL based on VITE_BACKEND_TARGET
 * Throws error if URL is not configured in environment variables
 */
export const getApiBaseUrl = (): string => {
  const target = getBackendTarget();

  switch (target) {
    case 'local':
      if (!import.meta.env.VITE_LOCAL_API_URL) {
        throw new Error('VITE_LOCAL_API_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_LOCAL_API_URL;
    case 'dev':
      if (!import.meta.env.VITE_DEV_API_URL) {
        throw new Error('VITE_DEV_API_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_DEV_API_URL;
    case 'staging':
      if (!import.meta.env.VITE_STAGING_API_URL) {
        throw new Error('VITE_STAGING_API_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_STAGING_API_URL;
    case 'prod':
      if (!import.meta.env.VITE_PROD_API_URL) {
        throw new Error('VITE_PROD_API_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_PROD_API_URL;
    default:
      throw new Error(`Invalid backend target: ${target}`);
  }
};

/**
 * Get the WebSocket URL based on VITE_BACKEND_TARGET
 * Throws error if URL is not configured in environment variables
 */
export const getWebSocketUrl = (): string => {
  const target = getBackendTarget();

  switch (target) {
    case 'local':
      if (!import.meta.env.VITE_LOCAL_WS_URL) {
        throw new Error('VITE_LOCAL_WS_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_LOCAL_WS_URL;
    case 'dev':
      if (!import.meta.env.VITE_DEV_WS_URL) {
        throw new Error('VITE_DEV_WS_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_DEV_WS_URL;
    case 'staging':
      if (!import.meta.env.VITE_STAGING_WS_URL) {
        throw new Error('VITE_STAGING_WS_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_STAGING_WS_URL;
    case 'prod':
      if (!import.meta.env.VITE_PROD_WS_URL) {
        throw new Error('VITE_PROD_WS_URL is not defined in .env.local');
      }
      return import.meta.env.VITE_PROD_WS_URL;
    default:
      throw new Error(`Invalid backend target: ${target}`);
  }
};

// ============================================================================
// API CONFIGURATION HELPERS
// ============================================================================

export const getApiConfig = (): {
  baseUrl: string;
  wsUrl: string;
  timeout: number;
  retries: number;
  target: BackendTarget;
} => {
  return {
    baseUrl: getApiBaseUrl(),
    wsUrl: getWebSocketUrl(),
    timeout: 10000, // 10 seconds default timeout
    retries: 3, // Default retry count
    target: getBackendTarget(),
  };
};

// ============================================================================
// DEBUGGING
// ============================================================================

export const logApiConfig = (): void => {
  if (import.meta.env.VITE_DEV_DEBUG_LOGGING === 'true') {
    console.group('🌐 API Configuration');
    console.log('Backend Target:', getBackendTarget().toUpperCase());
    console.log('API Base URL:', getApiBaseUrl());
    console.log('WebSocket URL:', getWebSocketUrl());
    console.log('Full Config:', getApiConfig());
    console.groupEnd();
  }
};
